"use client";

import * as React from "react";
import { scaleLinear } from "d3-scale";
import { FRAME_BUDGET } from "../../../data/viz";
import { useSize } from "../../../hooks";
import VizFrame from "./VizFrame";

const MARGIN = { top: 8, right: 12, bottom: 30, left: 96 };
const ROW_H = 42;
const GAP = 22;

/**
 * Stacked horizontal bars against a fixed ceiling.
 *
 * Two stacks rather than one, because Burst jobs run on worker threads: adding
 * them to the main-thread total would claim a load the frame never carries.
 */
export default function FrameBudget() {
  const [ref, size] = useSize<HTMLDivElement>();
  const [hover, setHover] = React.useState<string | null>(null);

  const w = Math.max(size.width, 280);
  const innerW = Math.max(w - MARGIN.left - MARGIN.right, 40);
  const stacks = FRAME_BUDGET.stacks;
  const h = MARGIN.top + MARGIN.bottom + stacks.length * ROW_H + GAP;

  const x = React.useMemo(
    () => scaleLinear().domain([0, FRAME_BUDGET.budgetMs]).range([0, innerW]),
    [innerW],
  );

  return (
    <VizFrame
      title="Where one frame goes"
      meta={`budget ${FRAME_BUDGET.budgetMs} ms · 60 fps`}
    >
      <div ref={ref} className="relative px-3 py-3">
        <svg
          width="100%"
          height={h}
          viewBox={`0 0 ${w} ${h}`}
          role="img"
          aria-label="Stacked bars. Main thread work totals about 9.3 milliseconds and worker-thread Burst jobs about 7 milliseconds, both inside a 16.67 millisecond frame budget."
        >
          <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
            {/* the ceiling */}
            <line
              x1={innerW}
              x2={innerW}
              y1={-4}
              y2={stacks.length * ROW_H + GAP - 6}
              stroke="rgb(var(--line) / 0.45)"
              strokeDasharray="4 3"
            />

            {stacks.map((stack, si) => {
              const y = si * (ROW_H + GAP * 0.35);
              let acc = 0;
              const total = stack.parts.reduce((s, p) => s + p.ms, 0);
              return (
                <g key={stack.name} transform={`translate(0,${y})`}>
                  <text
                    x={-12}
                    y={14}
                    textAnchor="end"
                    className="fill-[rgb(var(--muted))] text-[11px]"
                  >
                    {stack.name}
                  </text>
                  <text
                    x={-12}
                    y={28}
                    textAnchor="end"
                    className="fill-[rgb(var(--faint))] font-mono text-[10px]"
                  >
                    {total.toFixed(1)} ms
                  </text>

                  {stack.parts.map((part, pi) => {
                    const px = x(acc);
                    const pw = Math.max(x(part.ms) - x(0), 1);
                    acc += part.ms;
                    const key = `${stack.name}-${part.name}`;
                    const dim = hover !== null && hover !== key;
                    return (
                      <rect
                        key={part.name}
                        x={px}
                        y={2}
                        width={pw}
                        height={22}
                        rx={2}
                        fill="rgb(var(--accent))"
                        opacity={
                          dim
                            ? 0.16
                            : 0.32 + (1 - pi / stack.parts.length) * 0.5
                        }
                        onMouseEnter={() => setHover(key)}
                        onMouseLeave={() => setHover(null)}
                        style={{
                          cursor: "crosshair",
                          transition: "opacity .15s",
                        }}
                      >
                        <title>{`${part.name}: ${part.ms} ms`}</title>
                      </rect>
                    );
                  })}

                  {/* remaining headroom */}
                  <rect
                    x={x(total)}
                    y={2}
                    width={Math.max(innerW - x(total), 0)}
                    height={22}
                    rx={2}
                    fill="rgb(var(--line) / 0.09)"
                  />
                </g>
              );
            })}

            {/* axis */}
            <g transform={`translate(0,${stacks.length * ROW_H + GAP - 12})`}>
              {x.ticks(5).map((t) => (
                <text
                  key={t}
                  x={x(t)}
                  y={14}
                  textAnchor="middle"
                  className="fill-[rgb(var(--faint))] font-mono text-[10px]"
                >
                  {t}
                </text>
              ))}
              <text
                x={innerW}
                y={-2}
                textAnchor="end"
                className="fill-[rgb(var(--muted))] font-mono text-[10px]"
              >
                16.67 ms ceiling
              </text>
            </g>
          </g>
        </svg>

        <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
          {stacks
            .flatMap((s) => s.parts.map((p) => ({ ...p, stack: s.name })))
            .sort((a, b) => b.ms - a.ms)
            .map((p) => (
              <li
                key={`${p.stack}-${p.name}`}
                className="font-mono text-2xs text-faint"
                onMouseEnter={() => setHover(`${p.stack}-${p.name}`)}
                onMouseLeave={() => setHover(null)}
              >
                <span className="text-muted">{p.ms}</span> {p.name}
              </li>
            ))}
        </ul>
      </div>
    </VizFrame>
  );
}
